import {Injectable} from "@angular/core";
import {ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree} from "@angular/router";
import {Observable, of} from "rxjs";
import {map, catchError} from "rxjs/operators";
import {GqlCartProductService} from "./graphql/gql-cart-product.service";
import {LoggerService} from "../common/logger.service";
import {UsanaObject} from "../usana-object";
import {IProduct, IProductGroup, IProductResolverData} from "./product-models";
import {ProductUtil} from "../util/product-util";
import {Constants} from "../constants";
import {ProductRouteDataService} from "./product-route-data.service";



@Injectable({providedIn: 'root'})
export class ProductRouteGuard extends UsanaObject implements CanActivate {
    constructor(
        private gqlCartProductService: GqlCartProductService,
        private router: Router,
        private productRouteDataService: ProductRouteDataService,
        loggerService: LoggerService
    ) {
        super(loggerService);
    }

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
        const usanaIdParam = route.paramMap.get('usanaId');
        const readableUrlParam = route.paramMap.get('readableUrl');
        const locale = route.parent?.paramMap.get('locale') || Constants.DEFAULT_LOCALE;

        if (!usanaIdParam) {
            this.log.error("ProductRouteGuard: usanaId parameter is missing");
            this.productRouteDataService.setProductData('', {
                productGroup: null,
                product: null,
                notFound: true
            });
            return of(true);
        }

        const usanaId = usanaIdParam.indexOf('-') > -1 ? ProductUtil.convertUsanaIdToDot(usanaIdParam) : usanaIdParam;
        // This is only called from the RouteGuard so we can refresh the product data if it's older than 5 minutes.
        const cachedData: IProductResolverData | undefined = this.productRouteDataService.getProductDataWithTimerCheck(usanaId);
        if (cachedData !== undefined) {
            // If the usanaId was not found, and was cached, we can return true and the ProductComponent will handle the not found case.
            if (cachedData.notFound) {
                return of(true);
            }
            const result = this.checkRouteCorrection(
                cachedData.product,
                cachedData.productGroup,
                usanaIdParam,
                readableUrlParam,
                locale,
                usanaId,
                cachedData
            );
            // Return cached product because it's not expired.'
            return of(result);
        }

        //This runs if the product data is not cached, or if the cached data is expired.
        return this.gqlCartProductService.getCartPDP(usanaId, readableUrlParam).pipe(
            map((productGroup: IProductGroup) => {
                if (!productGroup) {
                    this.productRouteDataService.setProductData(usanaId, {
                        productGroup: null,
                        product: null,
                        notFound: true
                    });
                    return true;
                }

                // Load matching product or first product available
                let product: IProduct = null;
                if (!!productGroup.products && Object.values(productGroup.products).length > 0) {
                    product = ProductUtil.findMatchedProduct(usanaId, Object.values(productGroup.products));
                    if (!product && productGroup.products) {
                        product = productGroup.products[0];
                    }
                }

                if (!product) {
                    this.productRouteDataService.setProductData(usanaId, {
                        productGroup: null,
                        product: null,
                        notFound: true
                    });
                    return true;
                }

                const productData: IProductResolverData = {
                    productGroup,
                    product,
                    notFound: false
                };

                // Store product data in service BEFORE any redirect
                this.productRouteDataService.setProductData(usanaId, productData);

                // Check if route correction is needed
                return this.checkRouteCorrection(product, productGroup, usanaIdParam, readableUrlParam, locale, usanaId, productData);
            }),
            catchError((error: any) => {
                this.log.error("ProductRouteGuard: getCartPDP error", error);
                this.productRouteDataService.setProductData(usanaId, {
                    productGroup: null,
                    product: null,
                    notFound: true
                });
                return of(true);
            })
        );
    }

    private checkRouteCorrection(
        product: IProduct,
        productGroup: IProductGroup,
        usanaIdParam: string,
        readableUrlParam: string,
        locale: string,
        usanaProductId: string,
        productData: IProductResolverData
    ): boolean | UrlTree {
        const needsCorrection = product.usanaId !== usanaProductId || usanaIdParam.indexOf(".") > -1 ||
            readableUrlParam !== productGroup.seoCrossMarketUrl;

        if (needsCorrection) {
            const seoUsanaId = ProductUtil.convertUsanaIdToDash(product.usanaId);

            // Build the corrected URL segments
            const urlSegments = [locale, Constants.PRODUCT_STATE, seoUsanaId];
            if (!!productGroup.seoCrossMarketUrl && productGroup.seoCrossMarketUrl.length > 0) {
                urlSegments.push(productGroup.seoCrossMarketUrl);
            }

            // Data is already stored in service above, so just return UrlTree for clean navigation
            this.log.debug("ProductRouteGuard: Redirecting to corrected URL", urlSegments);
            return this.router.createUrlTree(urlSegments);
        }

        // Route is correct, allow navigation to continue
        // Data is already stored in service above
        return true;
    }
}

